// Global blog cache for admin navigation
class BlogCache {
  constructor() {
    this.cache = new Map();
    this.lastFetch = null;
    this.ttl = 5 * 60 * 1000; // 5 minutes TTL
    this.initialized = false;
    this.pendingFetches = new Map(); // Track pending fetch promises to prevent duplicates
  }

  // Initialize cache system
  init() {
    if (this.initialized) {
      return;
    }

    this.initialized = true;

    // Clear any old cache on init
    this.clear();
  }

  set(key, data) {
    this.cache.set(key, {
      data,
      timestamp: Date.now(),
    });
  }

  get(key) {
    const cached = this.cache.get(key);
    if (!cached) {
      return null;
    }

    // Check if cache is still valid
    if (Date.now() - cached.timestamp > this.ttl) {
      this.cache.delete(key);
      return null;
    }

    return cached.data;
  }

  // Store complete admin dashboard state
  setDashboardState(state) {
    this.set("admin-dashboard", {
      blogs: state.blogs,
      stats: state.stats,
      pagination: state.pagination,
      analytics: state.analytics,
      filters: {
        searchTerm: state.searchTerm,
        statusFilter: state.statusFilter,
        sortBy: state.sortBy,
        currentPage: state.currentPage,
      },
    });
  }

  getDashboardState() {
    return this.get("admin-dashboard");
  }

  // Prefetch and cache blog list
  async prefetchBlogs(params = {}) {
    const key = `blogs-${JSON.stringify(params)}`;
    const fetchKey = `fetch-${key}`;

    // Return cached if available
    const cached = this.get(key);
    if (cached) {
      return cached;
    }

    // Check if there's already a pending fetch for this same content
    if (this.pendingFetches.has(fetchKey)) {
      return await this.pendingFetches.get(fetchKey);
    }

    // Create and store the fetch promise
    const fetchPromise = this._fetchBlogsList(key, params);
    this.pendingFetches.set(fetchKey, fetchPromise);

    try {
      const result = await fetchPromise;
      return result;
    } finally {
      // Clean up the pending fetch
      this.pendingFetches.delete(fetchKey);
    }
  }

  async _fetchBlogsList(key, params) {
    try {
      const searchParams = new URLSearchParams({
        page: "1",
        limit: "10",
        ...params,
      });

      const response = await fetch(`/api/blogs?${searchParams}`);
      const data = await response.json();

      if (data.success) {
        this.set(key, data);
        return data;
      }
    } catch (error) {
      console.error("Error prefetching blogs:", error);
    }

    return null;
  }

  // ------------------------------
  // Blog content caching (by slug)
  // ------------------------------
  getCachedBlogContent(slug, isAdminPreview = false) {
    if (!slug) {
      return null;
    }

    // Try both cache keys - prioritize admin version if available
    const adminCacheKey = `blog-${slug}-admin`;
    const publicCacheKey = `blog-${slug}`;

    // If requesting admin preview, try admin cache first
    if (isAdminPreview) {
      const adminCached = this.get(adminCacheKey);
      if (adminCached) {
        return adminCached;
      }
      // Fallback to public cache if available (better than refetching)
      const publicCached = this.get(publicCacheKey);
      if (publicCached) {
        return publicCached;
      }
    } else {
      // For public requests, try public cache first, then admin as fallback
      const publicCached = this.get(publicCacheKey);
      if (publicCached) {
        return publicCached;
      }
      // If we have admin cache and the blog is visible, use it
      const adminCached = this.get(adminCacheKey);
      if (adminCached && adminCached.blog?.visibility === "visible") {
        return adminCached;
      }
    }

    return null;
  }

  async prefetchBlogContent(slug, isAdminPreview = false) {
    if (!slug) {
      return null;
    }

    const adminCacheKey = `blog-${slug}-admin`;
    const publicCacheKey = `blog-${slug}`;
    const cacheKey = isAdminPreview ? adminCacheKey : publicCacheKey;
    const fetchKey = `fetch-${slug}-${isAdminPreview}`;

    // Check if we already have this content cached (avoid duplicate fetches)
    const existingCache = this.getCachedBlogContent(slug, isAdminPreview);
    if (existingCache) {
      return existingCache;
    }

    // Check if there's already a pending fetch for this same content
    if (this.pendingFetches.has(fetchKey)) {
      return await this.pendingFetches.get(fetchKey);
    }

    // Create and store the fetch promise
    const fetchPromise = this._fetchBlogContent(
      slug,
      isAdminPreview,
      cacheKey,
      adminCacheKey
    );
    this.pendingFetches.set(fetchKey, fetchPromise);

    try {
      const result = await fetchPromise;
      return result;
    } finally {
      // Clean up the pending fetch
      this.pendingFetches.delete(fetchKey);
    }
  }

  async _fetchBlogContent(slug, isAdminPreview, cacheKey, adminCacheKey) {
    try {
      // Fetch blog by slug, include admin_preview parameter if needed
      const url = new URL(
        `/api/blogs/slug/${encodeURIComponent(slug)}`,
        window.location.origin
      );
      if (isAdminPreview) {
        url.searchParams.set("admin_preview", "true");
      }

      const response = await fetch(url.toString());
      const data = await response.json();

      if (data?.success && data?.data) {
        const result = { blog: data.data };
        this.set(cacheKey, result);

        // If this is a public request and blog is visible, also cache it for admin
        // to prevent future duplicate fetches
        if (!isAdminPreview && data.data.visibility === "visible") {
          this.set(adminCacheKey, result);
        }

        return result;
      }
    } catch (error) {
      console.error("Error prefetching blog content:", error);
    }

    return null;
  }

  // ---------------------------------
  // Related blogs caching (by blog id)
  // ---------------------------------
  getCachedRelatedBlogs(blogId) {
    if (!blogId) {
      return null;
    }
    const key = `related-${blogId}`;
    return this.get(key);
  }

  async prefetchRelatedBlogs(blogId, tags = []) {
    if (!blogId) {
      return null;
    }
    const key = `related-${blogId}`;

    const cached = this.get(key);
    if (cached) {
      return cached;
    }

    try {
      let relatedBlogs = [];
      const uniqueById = new Map();

      // Strategy: Try to find blogs with matching tags, then fallback to latest
      if (Array.isArray(tags) && tags.length > 0) {
        // Try each tag and collect matching blogs
        for (const tag of tags) {
          const searchParams = new URLSearchParams({
            limit: "10", // Get more to have options
            visibility: "visible",
            tag: tag,
          });

          // eslint-disable-next-line no-await-in-loop
          const response = await fetch(`/api/blogs?${searchParams.toString()}`);
          // eslint-disable-next-line no-await-in-loop
          const data = await response.json();

          if (data?.success && Array.isArray(data?.data)) {
            for (const blog of data.data) {
              if (blog.id !== blogId && !uniqueById.has(blog.id)) {
                uniqueById.set(blog.id, blog);
              }
            }
          }

          // Stop if we have enough blogs
          if (uniqueById.size >= 3) break;
        }
      }

      // Convert to array and sort by creation date (newest first)
      relatedBlogs = Array.from(uniqueById.values()).sort(
        (a, b) => new Date(b.createdAt) - new Date(a.createdAt)
      );

      // If we don't have enough related blogs, fetch latest visible blogs as fallback
      if (relatedBlogs.length < 3) {
        const searchParams = new URLSearchParams({
          limit: "6",
          visibility: "visible",
        });

        const response = await fetch(`/api/blogs?${searchParams.toString()}`);
        const data = await response.json();

        if (data?.success && Array.isArray(data?.data)) {
          for (const blog of data.data) {
            if (blog.id !== blogId && !uniqueById.has(blog.id)) {
              relatedBlogs.push(blog);
              if (relatedBlogs.length >= 3) break;
            }
          }
        }
      }

      // Take only first 3
      const result = { blogs: relatedBlogs.slice(0, 3) };
      this.set(key, result);
      return result;
    } catch (error) {
      console.error("Error prefetching related blogs:", error);
    }

    return null;
  }

  // Clear cache
  clear() {
    this.cache.clear();
    this.lastFetch = null;
    this.pendingFetches.clear();
  }

  // Clear specific key
  invalidate(key) {
    this.cache.delete(key);
  }

  // Check if we should prefetch (avoid too frequent requests)
  shouldPrefetch() {
    if (!this.lastFetch) {
      return true;
    }
    return Date.now() - this.lastFetch > 30000; // 30 seconds minimum between prefetches
  }

  updateLastFetch() {
    this.lastFetch = Date.now();
  }

  // Legacy method for compatibility with CacheInitializer and BlogsPage
  async prefetchBlogList(
    page = 1,
    searchTerm = "",
    selectedTag = "",
    isAdminPreview = false,
    language = null // Allow null to auto-detect from localStorage
  ) {
    // If language is explicitly null, don't fetch
    if (language === null) {
      return null;
    }

    // Auto-detect language from localStorage if not provided
    const detectedLanguage =
      language ||
      (typeof window !== "undefined"
        ? localStorage.getItem("keystone-language") || "en"
        : "en");

    const params = {
      page: String(page),
      limit: "9",
      locale: detectedLanguage,
    };

    // Add admin_preview parameter if needed
    if (isAdminPreview) {
      params.admin_preview = "true";
    } else {
      // Only filter by visibility if NOT admin preview
      params.visibility = "visible";
    }

    if (searchTerm) {
      params.search = searchTerm;
    }
    if (selectedTag) {
      params.tag = selectedTag;
    }

    return this.prefetchBlogs(params);
  }

  getCachedBlogList(
    page = 1,
    searchTerm = "",
    selectedTag = "",
    isAdminPreview = false,
    language = null // Allow null to auto-detect from localStorage
  ) {
    // If language is explicitly null, return null
    if (language === null) {
      return null;
    }

    // Auto-detect language from localStorage if not provided
    const detectedLanguage =
      language ||
      (typeof window !== "undefined"
        ? localStorage.getItem("keystone-language") || "en"
        : "en");

    const params = {
      page: String(page),
      limit: "9",
      locale: detectedLanguage,
    };

    // Add admin_preview parameter if needed
    if (isAdminPreview) {
      params.admin_preview = "true";
    } else {
      // Only filter by visibility if NOT admin preview
      params.visibility = "visible";
    }

    if (searchTerm) {
      params.search = searchTerm;
    }
    if (selectedTag) {
      params.tag = selectedTag;
    }

    const cached = this.getPrefetchedBlogs(params);

    return cached;
  }

  // Get prefetched blogs data for specific params
  getPrefetchedBlogs(params = {}) {
    const key = `blogs-${JSON.stringify(params)}`;
    return this.get(key);
  }

  // Check if dashboard has recent cached data
  hasFreshDashboardData() {
    const dashboardState = this.getDashboardState();
    if (!dashboardState) {
      return false;
    }

    // Check if cached less than 2 minutes ago
    const cached = this.cache.get("admin-dashboard");
    if (!cached) {
      return false;
    }

    const age = Date.now() - cached.timestamp;
    return age < 2 * 60 * 1000; // 2 minutes
  }

  // Mark that data has been modified and needs refresh
  markDataAsModified() {
    this.set("data-modified", true);
  }

  // Check if data was modified and needs refresh
  isDataModified() {
    return this.get("data-modified") === true;
  }

  // Clear modification flag
  clearModificationFlag() {
    this.invalidate("data-modified");
  }

  // Force refresh by invalidating all cache
  forceRefresh() {
    this.clear();
    this.markDataAsModified();
  }
}

// Global instance
const blogCache = new BlogCache();

export default blogCache;
